import moment, {Moment} from 'moment';
import {_adaptSisDateFormat, _adaptSisTimeFormat} from 'util/SisDateTimeCompat';
import {padStr} from 'util/StringUtil';
import {log} from 'common/log'

export const MomFmt = {
	DATE: "YYYYMMDD",
	DATESLASH: "YYYY/MM/DD",
	DATEISO: "YYYY-MM-DD",
	DATEPARSEFORMATS: ["YYYY/MM/DD", "YYYY-MM-DD", "YYYYMMDD", 'D.M.Y'],
	TIMEISO: "H:mm",
	TIME: "HHmmss",
	TIMEPARSEFORMATS: ["H:mm", "HH:mm", "HHmmss"],
	DATETIME_OUT: "YYYY-MM-DD [um] H:mm",
	DATETIMESS_OUT: "YYYY-MM-DD [um] H:mm:ss",
	DATE_LONG: "dddd[, ]D[. ]MMMM Y",
	DATE_WEEKDAY: "dddd[, ]D.M.Y",
	DATESHORTWEEKDAY: "dd[, ]D.M.Y",
	DATESHORT: "DD.MM.Y",
};

export type SisDate = string | number | Moment;
export type SisTime = string | number | Moment;
export type SisDateTime = Moment;
export type SisTimeOptionsType = { ignoreSeconds?: boolean, userInput?: boolean, forceHHmm?: boolean }
export type SisDuration = string | number | moment.Duration;
export type SisDurationOptionsType = { userInput?: boolean, forceHHmm?: boolean };

// BEGIN moment helper funcs
/**
 * parseSisDate
 * @param {number|string|Moment} date Any format
 * @returns {Moment} cloned if {Moment} to prevent unwanted manipulation of original object
 */
export const parseSisDate = (date: SisDate): Moment => {
	if (moment.isMoment(date)) return moment(date); //clone
	// noinspection EqualityComparisonWithCoercionJS
	if (!date || date == 0) {
		// force invalid date on
		date = "";
	}
	let adapted = _adaptSisDateFormat("" + date); //try apply SisDate Format rules
	return moment("" + (adapted ? adapted : date), MomFmt.DATEPARSEFORMATS);
};
/**
 * Date in number format - persistable and for model usage
 * @param date Any format
 * @returns {Number} YYYYMMDD
 */
export const getSisDateAsNumber = (date: SisDate) => {
	if (!moment.isMoment(date)) date = parseSisDate(date);
	return parseInt(date.format(MomFmt.DATE))
};
/**
 * getDateAsISO
 * @param date Any format
 * @returns {string} ISO date format (e.g. input[type=date])
 */
export const getSisDateAsISO = (date: SisDate) => {
	if (!moment.isMoment(date)) date = parseSisDate(date);
	return date.format(MomFmt.DATEISO);
};

/**
 * getDateAsShort
 * @param date Any format
 * @returns {string} SHORT date format (e.g. input[type=date], 01.01.2022)
 */
export const getSisDateAsShort = (date: SisDate) => {
	if (!moment.isMoment(date)) date = parseSisDate(date);
	return date.format(MomFmt.DATESHORT);
};

/**
 * Common format for visualizing dates.
 * @param date Any format
 * @return {String} YYYY/MM/DD
 */
export const getSisDateAsString = (date: SisDate) => {
	if (!moment.isMoment(date)) date = parseSisDate(date);
	return date.format(MomFmt.DATESLASH);
};


/**
 * @param {number|string|Moment} time HHmmss, Hmmss or HH:mm
 * @param {{}} [options]
 * @param {boolean} [options.ignoreSeconds] drops seconds from result
 * @param {boolean} [options.userInput] always appends 00 seconds to input if not contained. this is required because user input of 11 is not equivalent to db-value 11 (11 minutes vs 11 seconds). When set to true, it may make sense to also use options.ignoreSeconds
 * @returns {Moment} cloned if time is a {Moment} to prevent unwanted manipulation of original object
 */
export const parseSisTime = (time: SisTime, options?: SisTimeOptionsType): Moment => {
	// time can be 0
	var result;
	if (moment.isMoment(time)) {
		//clone
		result = moment(time);
	} else {
		let input = "" + time;
		if (input === "*") {
			result = moment();
		} else {
			//old epze-like adapting
			if (options && options.userInput) {
				input = _adaptSisTimeFormat(input, true) || input;
			}
			//if not adapted or exact input try sis-db-format
			if (input.indexOf(":") == -1) {
				input = padStr(input, 6);
			} //sisformat HHmmss
			result = moment(input, MomFmt.TIMEPARSEFORMATS);
		}
	}
	if (options && options.ignoreSeconds) {
		result.startOf('minute');
	}
	return result;
};
/**
 * @param time Any format from parseSisTime
 * @param {{}} [options] any option from parseSisTime
 * @param {boolean} [options.forceHHmm] forces 5 digit format for type=time fields
 * @param {boolean} [options.ignoreSeconds] drops seconds from result
 * @param {boolean} [options.userInput] always appends 00 seconds to input if not contained. this is required because user input of 11 is not equivalent to db-value 11 (11 minutes vs 11 seconds). When set to true, it may make sense to also use options.ignoreSeconds * @returns {string} in format "H:mm"
 */
export const getSisTimeAsISO = (time: SisTime, options?: SisTimeOptionsType) => {
	if (!moment.isMoment(time)) time = parseSisTime(time, options);
	time = time.format(MomFmt.TIMEISO);
	if (options && options.forceHHmm === true) time = padStr(time, 5);
	return time;
};
export const getSisTimeAsNumber = (time: SisTime, options?: SisTimeOptionsType) => {
	if (!moment.isMoment(time)) time = parseSisTime(time, options);
	return parseInt(time.format(MomFmt.TIME));
};

function validatePulse(pulse?: moment.Duration) {
	const valid = moment.isDuration(pulse) && pulse.isValid() && pulse.asMinutes() >= 1
	if (!valid && pulse && +pulse < 1000) log.error('roundSisTime: pulse used (value = ' + pulse + ') but it is less than 2 minutes and therefore has no effect!')
	return valid
}

export enum RoundMode {
	floor = 'floor',
	'<' = 'floor',
	ceil = 'ceil',
	'>' = 'ceil',
	nearest = 'nearest',
	'=' = 'nearest'
}

export function roundSisTime(time: SisTime, pulse: SisDuration, mode: RoundMode) {
	const _mode = mode
	mode = RoundMode[mode] // ensure correct value (floor / ceil)
	if (!moment.isMoment(time)) time = parseSisTime(time);
	if (!moment.isDuration(pulse)) pulse = parseSisDuration(pulse);

	if (!time.isValid() || !validatePulse(pulse)) return time;

	var pulseSek = pulse.asSeconds();
	var refTime = moment(time);
	refTime.seconds(0);
	refTime.minutes(0);
	refTime.hours(0);

	var diff = time.diff(refTime, 'seconds');
	var offset = diff % pulseSek;

	if (!offset) return time; //nothing to change, matches value

	if (mode === RoundMode.nearest) {
		mode = pulseSek - offset < pulseSek / 2 ? RoundMode.ceil : RoundMode.floor
	}

	if (mode === RoundMode.floor) {
		time = time.subtract(offset, 'seconds'); //floor
	} else if (mode === RoundMode.ceil) {
		time = time.add(pulseSek - offset, 'seconds'); //ceil
	} else throw "No mode set. Use round mode, but not: " + _mode;
	return time;
}

export function roundSisDuration(dur: SisDuration, pulse: SisDuration, mode: RoundMode) {
	mode = RoundMode[mode] // ensure correct value (floor / ceil)
	if (!moment.isDuration(dur)) dur = parseSisDuration(dur);
	if (!moment.isDuration(pulse)) pulse = parseSisDuration(pulse);

	if (!dur.isValid() || !validatePulse(pulse)) return dur;

	let asSek = dur.asSeconds();
	const pulseSek = pulse.asSeconds();
	const rest = asSek % pulseSek
	asSek = asSek - rest; //floor
	if (mode === RoundMode.ceil || mode === RoundMode.nearest && pulseSek - rest < pulseSek / 2) {
		asSek += pulseSek; //ceil
	}

	return moment.duration(asSek * 1000);
}

/**
 * Precision: seconds
 * @param {number|string|Moment}time
 * @param {number|string|Moment.duration} pulse in Moment acceptable iso format or HHmmss
 * @returns {Moment}
 */
export const floorSisTime = (time: SisTime, pulse: SisDuration) => {
	return roundSisTime(time, pulse, RoundMode.floor);
};
/**
 * Precision: seconds
 * @param {number|string|Moment}time
 * @param {number|string|Moment.duration} pulse in Moment acceptable iso format or HHmmss
 * @returns {Moment}
 */
export const ceilSisTime = (time: SisTime, pulse: SisDuration) => {
	return roundSisTime(time, pulse, RoundMode.ceil);
};

/**
 * Precision: seconds
 * @param {number|string|Moment.duration} dur sis-duration
 * @param {number|string|Moment.duration} pulse in Moment acceptable iso format or HHmmss
 * @returns {Moment}
 */
export const floorSisDuration = (dur: SisDuration, pulse: SisDuration) => {
	return roundSisDuration(dur, pulse, RoundMode.floor);
};
/**
 * Precision: seconds
 * @param {number|string|Moment.duration} dur sis-duration
 * @param {number|string|Moment.duration} pulse in Moment acceptable iso format or HHmmss
 * @returns {Moment}
 */
export const ceilSisDuration = (dur: SisDuration, pulse: SisDuration) => {
	return roundSisDuration(dur, pulse, RoundMode.ceil);
};

/**
 * todo merge implementation with parseSisDate
 *
 * helper function to merge sisdate/sistime to one moment object
 * ignores milliseconds
 * @param date see parseSisDate
 * @param time see parseSisTime
 * @param options
 * @returns {Moment}
 */
export const parseSisDateTime = (date: SisDate, time?: SisTime, options?: SisTimeOptionsType) => {
	date = parseSisDate(date);
	if (time != null) {
		time = parseSisTime(time, options);
		date.hour(time.hour());
		date.minute(time.minute());
		date.second(time.second());
	}
	return date;
};
export const getSisDateTimeAsString = (date: SisDate, time?: SisTime, options?: SisTimeOptionsType) => {
	return parseSisDateTime(date, time, options).format(MomFmt.DATETIME_OUT);
};

/**
 * use SisDuration for application
 * @param {number|string|Moment.duration} duration in Moment acceptable iso format HHHH:mm:ss or HHHHmmss
 * @param {{}} [options]
 * @param {boolean} [options.userInput=false] false: db format
 * @returns {Moment.Duration} cloned if {Moment} to prevent unwanted manipulation of original object
 */
export const parseSisDuration = (duration: SisDuration, options?: SisDurationOptionsType) => {
	if (!duration) return moment.duration(0);
	if (moment.isDuration(duration)) {
		if (!duration.isValid()) return moment.duration(NaN); // invalid
		else return moment.duration(duration); //clone
	}
	let out = (duration + '').trim();
	let negative = out[0] === '-';
	if (negative) out = out.substr(1, out.length);
	if (options && options.userInput) {
		if (out.indexOf(':') < 0) {
			// autocomplete format
			if (out.length < 3) out += '0000'; // h,hh
			else out += '00'; // hmm, hhmm
		} else {
			// explicit format
			let components = out.split(':');
			out = (components[0] || '') + padStr(components[1] || 0, 2) + '00';
		}
	}
	//convert SIS format to duration format
	let ret = moment.duration({
		hours: parseInt(out.substring(0, out.length - 4)) || 0,
		minutes: parseInt(out.substring(out.length - 4, out.length - 2)) || 0,
		seconds: parseInt(out.substring(out.length - 2, out.length)) || 0,
	});
	if (negative && ret.isValid()) return moment.duration(0).subtract(ret);
	return ret;
};
/**
 * @param duration Any format from parseSisDuration
 * @param {{}} [options] any option from parseSisDuration
 * @param {boolean} [options.forceHHmm] forces 5 digit format for type=time fields
 * @param {boolean} [options.userInput=false] false: db format
 * @returns {string} in format "H:mm"
 */
export const getSisDurationAsISO = (duration: SisDuration, options?: SisDurationOptionsType) => {
	if (!moment.isDuration(duration)) duration = parseSisDuration(duration, options);
	if (!duration.isValid()) return '';
	let durationString = (+duration) < 0 ? "-" : "";
	durationString += Math.floor(Math.abs(duration.asHours()));
	durationString += ":";
	durationString += padStr(Math.abs(duration.minutes()), 2);
	if (options && options.forceHHmm === true) durationString = padStr(durationString, 5);
	return durationString;
};

/**
 * parameter seconds is converted in SisDuration.
 * @param {number|string|Moment.duration} duration duration
 * @param {{}} [options] any option from parseSisDuration
 * @param {boolean} [options.userInput=false] false: db format
 * @returns {number} SisDuration
 */
export function getSisDurationAsNumber(duration: SisDuration, options?: SisDurationOptionsType) {
	if (!moment.isDuration(duration)) duration = parseSisDuration(duration, options);
	if (!duration.isValid()) return NaN;
	let ret = (+duration) < 0 ? "-" : "";
	ret += Math.floor(Math.abs(duration.asHours())); // rounding towards zero
	ret += padStr(Math.abs(duration.minutes()), 2);
	ret += padStr(Math.abs(duration.seconds()), 2);
	return Number(ret);
}

export function getSisDurationBetween24H(timeFrom: SisTime, timeTo: SisTime) {
	//ensure same date
	const from = parseSisDateTime(20220101, timeFrom)
	const to = parseSisDateTime(20220101, timeTo)
	if (!from.isValid() || !to.isValid()) return 0
	if (to.isBefore(from)) to.add(1, 'days')
	return moment.duration(to.diff(from))
}